import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import {
    DndContext,
    DragOverlay,
    useSensors,
    useSensor,
    PointerSensor,
    KeyboardSensor
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { useDroppable, useDraggable } from '@dnd-kit/core'; // For Waiting List
import { CSS } from '@dnd-kit/utilities';

import { useToast } from '../../hooks/useToast';
import { usePermissions } from '../../hooks/usePermissions';
import { useDataScope } from '../../hooks/useDataScope';
import { PERMISSIONS } from '../../constants/permissions';
import ReservationModal from '../../components/business/ReservationModal';
import { getDailyReservations, createReservation, updateReservation, deleteReservation, getWaitingReservations, addReservationNote } from '../../services/business/reservationService';
import { calculateAutoAssignments } from '../../services/business/planningService';
import { logAuditAction } from '../../services/logging/auditLogger';
import AutoAssignModal from '../../components/planning/AutoAssignModal';
import ConfirmDialog from '../../components/shared/ConfirmDialog';

import { getDocks, createDock } from '../../services/business/dockService';
import { getAllFactories } from '../../services/firestore/factoryService';
import { getDockTypes, getConfigsByCategory, getGeneralSettings } from '../../services/firestore/systemConfigService';
import { getDoorGroupsByFactory, getAllDoorGroups } from '../../services/firestore/doorGroupService';
import { isTransporterMatch, buildUserIdentities, buildTransporterIdentities } from '../../services/business/transporterMatchingService';
import { getAllTransporters } from '../../services/firestore/transporterService';
import { CONFIG_CATEGORIES } from '../../constants/systemConfig';
import { VEHICLE_STATUS, STATUS_LABELS, STATUS_COLORS } from '../../constants/statuses';
import '../../styles/components/fullcalendar.css';
import '../../styles/components/timeline.css';

import TimelineBoard from '../../components/timeline/TimelineBoard';
import TimelineEvent from '../../components/timeline/TimelineEvent'; // For Overlay

// Config - moved to state for responsiveness
const HOURS_START = 8;

// Prepare STATUS_CONFIG for TimelineBoard from centralized constants
const STATUS_CONFIG = Object.keys(STATUS_LABELS).reduce((acc, key) => {
    acc[key] = {
        label: STATUS_LABELS[key],
        color: STATUS_COLORS[key] || '#94a3b8',
        textColor: '#fff'
    };
    return acc;
}, {});

// -- Draggable Waiting Item Component --
const DraggableWaitingItem = ({ res }) => {
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
        id: `waiting-${res.id}`,
        data: { type: 'waiting', reservation: res }
    });

    const style = transform ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        zIndex: 1000,
    } : undefined;

    return (
        <div
            ref={setNodeRef}
            {...listeners}
            {...attributes}
            className={`cursor-grab transition-all hover:bg-white/10 p-4 rounded-xl border shadow-lg ${isDragging ? 'opacity-0' : 'opacity-100'}`}
            style={{
                ...style,
                background: 'var(--bg-card)',
                backdropFilter: 'blur(10px)',
                border: '1px solid var(--border-color)',
                boxShadow: 'var(--shadow-md)',
                marginBottom: '10px',
                position: transform ? 'relative' : 'static' /* Ensure style is spread correctly */
            }}
        >
            <div className="flex justify-between items-start" style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontWeight: '800', fontSize: '13px', color: 'var(--text-primary)', letterSpacing: '0.5px' }}>
                        {res.plateNumber || 'PLAKA BELİRSİZ'}
                    </span>
                    <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: '600', marginTop: '2px' }}>
                        {res.transporterShortName || 'Taşıyıcı Belirsiz'}
                    </span>
                </div>
                <div style={{
                    background: 'var(--bg-secondary)',
                    padding: '4px 8px',
                    borderRadius: '6px',
                    fontSize: '11px',
                    fontWeight: '800',
                    color: 'var(--primary-color)',
                    border: '1px solid var(--border-color)'
                }}>
                    {dayjs().format('HH:mm')}
                </div>
            </div>

            {/* Metrics Grid */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '8px',
                marginTop: '12px',
                paddingTop: '12px',
                borderTop: '1px solid rgba(255, 255, 255, 0.05)'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '14px' }}>📦</span>
                    <span style={{ fontSize: '11px', color: 'var(--text-primary)', fontWeight: '800' }}>
                        {res.metrics?.volume || res.plannedVolume || 0} m³
                    </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '14px' }}>⚖️</span>
                    <span style={{ fontSize: '11px', color: 'var(--text-primary)', fontWeight: '800' }}>
                        {res.metrics?.weight || res.plannedWeight || 0} ton
                    </span>
                </div>
            </div>
        </div>
    );
};

// -- Waiting List Droppable Area --
const WaitingListArea = ({ children, isOver }) => {
    const { setNodeRef } = useDroppable({ id: 'waiting-list' });
    return (
        <div
            ref={setNodeRef}
            className="space-y-3 p-4"
            style={{
                flex: 1,
                overflowY: 'auto',
                background: isOver ? 'rgba(59, 130, 246, 0.05)' : 'transparent',
                maxHeight: '100%',
                scrollBehavior: 'smooth'
            }}
        >
            {children}
        </div>
    );
};

const ReservationManagement = ({ userData }) => {
    const { success, error: toastError, warning } = useToast();
    const { user, can } = usePermissions(userData);
    const scope = useDataScope(userData);
    const navigate = useNavigate();
    const timelineRef = useRef(null); // For scrolling
    const timelineWrapperRef = useRef(null); // For measuring width

    // State
    const [selectedDate, setSelectedDate] = useState(dayjs().format('YYYY-MM-DD'));
    const [reservations, setReservations] = useState([]);
    const [waitingList, setWaitingList] = useState([]);
    const [docks, setDocks] = useState([]);
    const [pxPerMin, setPxPerMin] = useState(2); // Dynamic zoom for responsive view

    // Responsive Timeline Logic - 8 hours fits exactly to container width
    useEffect(() => {
        const updateWidth = () => {
            if (timelineWrapperRef.current) {
                const totalWidth = timelineWrapperRef.current.clientWidth;
                const contentWidth = totalWidth - 80; // Sidebar is 80px
                if (contentWidth > 50) { // Minimum reasonable width
                    // 8 hours (480 mins) fits exactly in content width
                    const newPxPerMin = contentWidth / (8 * 60);
                    setPxPerMin(newPxPerMin);
                }
            }
        };

        // Initial updates with retries
        updateWidth();
        const timer1 = setTimeout(updateWidth, 50);
        const timer2 = setTimeout(updateWidth, 150);
        const timer3 = setTimeout(updateWidth, 300);

        // ResizeObserver for immediate updates
        const observer = new ResizeObserver(() => {
            requestAnimationFrame(updateWidth);
        });

        if (timelineWrapperRef.current) {
            observer.observe(timelineWrapperRef.current);
        }

        window.addEventListener('resize', updateWidth);

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', updateWidth);
            clearTimeout(timer1);
            clearTimeout(timer2);
            clearTimeout(timer3);
        };
    }, []);

    // Resource State
    const [factories, setFactories] = useState([]);
    const [doorGroups, setDoorGroups] = useState([]);
    const [dockTypes, setDockTypes] = useState([]);
    const [vehicleTypes, setVehicleTypes] = useState([]);
    const [loadingRules, setLoadingRules] = useState([]); // [NEW] Loading rules for duration calculation
    const [systemConfig, setSystemConfig] = useState(null); // [NEW] System settings for slotDuration

    // UI State
    const [activeMenuId, setActiveMenuId] = useState(null);
    const [menuPos, setMenuPos] = useState({ x: 0, y: 0 });
    const [dragActiveItem, setDragActiveItem] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalData, setModalData] = useState(null);
    const [isAutoAssignModalOpen, setIsAutoAssignModalOpen] = useState(false);
    const [confirmConfig, setConfirmConfig] = useState({
        isOpen: false,
        title: '',
        message: '',
        type: 'warning',
        confirmText: 'Onayla',
        cancelText: 'Vazgeç',
        onConfirm: () => { }
    });

    const showConfirm = (config) => {
        setConfirmConfig({
            isOpen: true,
            title: config.title || 'Onay Gerekiyor',
            message: config.message || 'Bu işlemi yapmak istediğinize emin misiniz?',
            type: config.type || 'warning',
            confirmText: config.confirmText || 'Evet, Onayla',
            cancelText: config.cancelText || 'İptal',
            onConfirm: () => {
                config.onConfirm();
                setConfirmConfig(prev => ({ ...prev, isOpen: false }));
            }
        });
    };

    const [pendingAutoAssignments, setPendingAutoAssignments] = useState([]);

    // Initial View Limits
    const [viewStartHour, setViewStartHour] = useState(dayjs().hour()); // Start at current hour instead of fixed 18:00

    // Filter State
    const [filterFactory, setFilterFactory] = useState('all');
    const [filterType, setFilterType] = useState('all'); // This is actually DoorGroup Filter
    const [filterDock, setFilterDock] = useState('all');

    // Filter Logic
    const filteredDocks = docks.filter(d => {
        // --- UI FILTERING (User Dropdowns) ---
        // These apply to everyone
        if (filterFactory !== 'all' && d.factoryId !== filterFactory) return false;

        if (filterType !== 'all') {
            if (d.doorGroupId && d.doorGroupId !== filterType) return false;
            // Fallback for missing doorGroup (match by type code if legacy)
            if (!d.doorGroupId && d.type !== filterType) return false;
        }

        if (filterDock !== 'all' && d.id !== filterDock) return false;

        // --- DATA SCOPE FILTERING (Vertical/Permissions) ---
        if (!scope.isFullAccess) {
            // Priority 1: If user has an unmasked reservation here (Timeline or Waiting), SHOW IT
            const hasUserReservation = [...reservations, ...waitingList].some(r => r.dockId === d.id && !r.isMasked);
            if (hasUserReservation) return true;

            // Priority 2: Check explicit scope restrictions
            // If they have explicit DOCK IDs in scope, must match one of them
            if (scope.dockIds.length > 0) {
                return scope.dockIds.includes(d.id);
            }

            // If they have explicit DOOR GROUP IDs in scope, must match one
            if (scope.doorGroupIds.length > 0) {
                if (!d.doorGroupId || !scope.doorGroupIds.includes(d.doorGroupId)) return false;
            }

            // If they have explicit FACTORY IDs in scope, must match one
            if (scope.factoryIds.length > 0) {
                if (!d.factoryId || !scope.factoryIds.includes(d.factoryId)) return false;
            }

            // Transporter specific: if they are a transporter and we haven't blocked them yet, 
            // allow seeing docks in the factory they are viewing to allow planning.
            if (user?.role === 'transporter') return true;
        }

        return true;
    }).sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));

    // Load Initial Data including Factories
    useEffect(() => {
        if (user?.tenantId) {
            loadMetaData(user.tenantId);
            loadData(user.tenantId);
            loadDockTypes(user.tenantId);
        }
    }, [selectedDate, user]);

    const loadDockTypes = async (tenantId) => {
        try {
            const types = await getDockTypes(tenantId);
            setDockTypes(types);
        } catch (e) { console.error('Dock types load error', e); }
    };

    useEffect(() => {
        // Load door groups when factory changes
        if (user?.tenantId) {
            loadDoorGroups(user.tenantId, filterFactory);
        }
    }, [filterFactory, user]);

    useEffect(() => {
        dayjs.locale('tr');
    }, []);

    const loadMetaData = async (tenantId) => {
        try {
            const [factoryDataRaw, vtData, rulesData, settings] = await Promise.all([
                getAllFactories(tenantId, true),
                getConfigsByCategory(tenantId, CONFIG_CATEGORIES.VEHICLE_TYPES),
                getConfigsByCategory(tenantId, CONFIG_CATEGORIES.LOADING_RULES),
                getGeneralSettings(tenantId)
            ]);

            let factoryData = factoryDataRaw;
            setVehicleTypes(vtData);
            setLoadingRules(rulesData);
            setSystemConfig(settings);

            // Scope Filter
            if (!scope.isFullAccess && scope.factoryIds.length > 0) {
                factoryData = factoryData.filter(f => scope.factoryIds.includes(f.id));
            }

            setFactories(factoryData);
            // Optionally set default factory if none selected
            if (filterFactory === 'all' && factoryData.length > 0) {
                setFilterFactory(factoryData[0].id);
            }
        } catch (err) {
            console.error("Meta data load error", err);
        }
    }

    const loadDoorGroups = async (tenantId, factoryId) => {
        try {
            let groups = [];
            if (factoryId === 'all') {
                groups = await getAllDoorGroups(tenantId, true);
            } else {
                groups = await getDoorGroupsByFactory(tenantId, factoryId, true);
            }

            // Scope Filter
            if (!scope.isFullAccess && scope.doorGroupIds.length > 0) {
                groups = groups.filter(g => scope.doorGroupIds.includes(g.id));
            }

            setDoorGroups(groups);
        } catch (err) {
            console.error(err);
        }
    }

    const loadData = async (tenantId) => {
        try {
            const [resData, dockData, waitingData] = await Promise.all([
                getDailyReservations(tenantId, selectedDate),
                getDocks(tenantId),
                getWaitingReservations(tenantId)
            ]);

            // Resolve identities for transporter
            const myIdentities = buildUserIdentities(userData);
            if (userData?.role === 'transporter') {
                try {
                    const tData = await getAllTransporters(tenantId);
                    tData?.forEach(t => {
                        const tIdentities = buildTransporterIdentities(t);
                        if (Array.from(tIdentities).some(tid => myIdentities.has(tid))) {
                            tIdentities.forEach(id => myIdentities.add(id));
                        }
                    });
                } catch (e) { console.warn('Identity enrichment failed', e); }
            }

            // Apply Masking & Status Filtering Logic
            const processReservations = (data) => data
                .filter(res => {
                    // Filter out cancelled/rejected from timeline view
                    const isCancelled = res.status === VEHICLE_STATUS.CANCELLED || res.status === 'cancelled' || res.status === 'rejected';
                    return !isCancelled;
                })
                .map(res => {
                    // Transporter can only see details of their own reservations
                    const isTransporter = user?.role === 'transporter';
                    if (!isTransporter) return { ...res, isMasked: false };

                    // Use robust matching service
                    const isOwner = isTransporterMatch(res, myIdentities);
                    return { ...res, isMasked: !isOwner };
                });

            setDocks(dockData);
            setReservations(processReservations(resData));
            setWaitingList(processReservations(waitingData));
        } catch (err) {
            console.error(err);
        }
    };

    // Auto Assign Logic
    const handleAutoAssign = async (filters) => {
        try {
            const proposedAssignments = calculateAutoAssignments(
                waitingList,
                reservations,
                filteredDocks, // [FIX] Strictly use visible docks
                {
                    date: filters.date,
                    mode: filters.mode,
                    startHour: filters.startHour,
                    minDelayMinutes: filters.minDelayMinutes,
                    slotStep: systemConfig?.slotDuration ? parseInt(systemConfig.slotDuration) : 30,
                    dockTypes: dockTypes,
                    vehicleTypes: vehicleTypes,
                    loadingRules: loadingRules // [NEW] Pass for on-the-fly duration calculation
                }
            );

            if (proposedAssignments.length === 0) {
                warning('Seçilen kriterlere uygun atama yapılamadı (Rampa tipi veya boşluk uymuyor).');
                return;
            }

            setPendingAutoAssignments(proposedAssignments);
            showConfirm({
                title: 'Otomatik Planlama Onayı',
                message: `${proposedAssignments.length} adet rezervasyon otomatik olarak planlanacak. Onaylıyor musunuz?`,
                type: 'info',
                confirmText: 'Evet, Planla',
                onConfirm: executeAutoAssign
            });

        } catch (e) {
            console.error(e);
            toastError("Otomatik atama sırasında hata oluştu.");
        }
    };

    const executeAutoAssign = async () => {
        try {

            const promises = pendingAutoAssignments.map(proposal => {
                const { reservation, slot } = proposal;
                return updateReservation(reservation.id, {
                    dockId: slot.dockId,
                    dockName: slot.dockName,
                    startTime: slot.startTime,
                    endTime: slot.endTime,
                    status: VEHICLE_STATUS.PENDING_APPROVAL
                }, userData, userData.tenantId);
            });

            await Promise.all(promises);

            await logAuditAction(
                'batch',
                'RESERVATION',
                'AUTO_ASSIGN',
                { count: pendingAutoAssignments.length },
                userData,
                'AutoAssignment',
                userData.tenantId
            );

            success(`${pendingAutoAssignments.length} rezervasyon başarıyla atandı.`);
            loadData(userData.tenantId);
            setIsAutoAssignModalOpen(false);
            setPendingAutoAssignments([]);

        } catch (e) {
            console.error(e);
            toastError("Atama uygulanırken hata oluştu.");
        }
    };

    // DND Sensors
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    // DND Handlers
    const handleDragStart = (event) => {
        setDragActiveItem(event.active.data.current?.reservation);
    };

    const handleDragEnd = async (event) => {
        // PERMISSION CHECK
        if (!can(PERMISSIONS.RESERVATIONS, PERMISSIONS.EDIT)) {
            setDragActiveItem(null);
            warning('Düzenleme yetkiniz bulunmamaktadır.');
            return;
        }

        const { active, over } = event;
        setDragActiveItem(null);

        if (!over) return;

        // 1. Drop to Waiting List
        if (over.id === 'waiting-list') {
            if (active.data.current?.type === 'reservation') {
                const res = active.data.current.reservation;
                handleMoveToWaiting(res);
            }
            return;
        }

        // 2. Drop to Dock (Timeline Row)
        if (String(over.id).startsWith('dock-')) {
            const dockId = String(over.id).replace('dock-', '');
            const dock = docks.find(d => d.id === dockId);
            const res = active.data.current?.reservation;

            if (dock && res) {
                // DOCK TYPE COMPATIBILITY CHECK
                const isTransporter = userData?.role === 'transporter';
                if (isTransporter) {
                    const dockTypeCfg = dockTypes.find(dt => dt.code === dock.type);
                    // Resolve vehicle type code
                    let vCode = res.metrics?.vehicleTypeCode || res.vehicleTypeCode;
                    if (!vCode && res.metrics?.vehicleType && vehicleTypes.length > 0) {
                        const vt = vehicleTypes.find(t => t.id === res.metrics.vehicleType);
                        if (vt) vCode = vt.code;
                    }

                    if (vCode && dockTypeCfg && dockTypeCfg.allowedVehicleTypes?.length > 0) {
                        if (!dockTypeCfg.allowedVehicleTypes.includes(vCode)) {
                            warning(`Bu rampa tipi (${dock.type}), seçilen araç tipi (${vCode}) için uygun değildir.`);
                            return;
                        }
                    }
                }

                // Calculate Time
                const droppableNode = document.getElementById(`dock-${dockId}`);
                // Calculate dropped time
                const activeRect = active.rect.current.translated;
                const dropRect = over.rect;

                let relativeX = activeRect.left - dropRect.left;
                if (relativeX < 0) relativeX = 0;

                // visibleStart is `selectedDate` + `viewStartHour`
                const visibleStartMs = dayjs(selectedDate).hour(viewStartHour).valueOf();
                const offsetMs = (relativeX / pxPerMin) * 60000;

                // Snap to 15 mins
                const rawTime = visibleStartMs + offsetMs;
                const snappedTime = Math.round(rawTime / (15 * 60000)) * (15 * 60000);

                let newStartTime = new Date(snappedTime);
                // Ensure duration is rounded to minutes to prevent millisecond noise
                const durationMs = (new Date(res.endTime) - new Date(res.startTime)) || 3600000;
                const durationMins = Math.round(durationMs / 60000);
                let newEndTime = new Date(snappedTime + (durationMins * 60000));

                // Optimistic Update
                if (active.data.current.type === 'waiting') {
                    // Remove from waiting, add to reservations
                    setWaitingList(prev => prev.filter(r => r.id !== res.id));
                    const newRes = { ...res, dockId: dock.id, dockName: dock.name, startTime: newStartTime, endTime: newEndTime, status: VEHICLE_STATUS.PENDING_APPROVAL };
                    setReservations(prev => [...prev, newRes]);

                    await updateReservation(res.id, {
                        dockId: dock.id,
                        dockName: dock.name,
                        startTime: newStartTime,
                        endTime: newEndTime,
                        status: VEHICLE_STATUS.PENDING_APPROVAL
                    }, userData, userData.tenantId);

                    loadData(userData.tenantId);
                } else {
                    // Move existing reservation
                    setReservations(prev => prev.map(r => r.id === res.id ? { ...r, dockId: dock.id, dockName: dock.name, startTime: newStartTime, endTime: newEndTime } : r));
                    await updateReservation(res.id, {
                        dockId: dock.id,
                        dockName: dock.name,
                        startTime: newStartTime,
                        endTime: newEndTime
                    }, userData, userData.tenantId);
                }
            }
        }
    };

    // Actions
    const handleMoveToWaiting = async (res) => {
        if (!can(PERMISSIONS.RESERVATIONS, PERMISSIONS.EDIT)) {
            warning('Düzenleme yetkiniz bulunmamaktadır.');
            return;
        }
        showConfirm({
            title: 'Bekleyenlere Taşı',
            message: 'Bu rezervasyonu bekleyen listesine taşımak istediğinize emin misiniz?',
            onConfirm: async () => {
                setReservations(prev => prev.filter(r => r.id !== res.id));
                setWaitingList(prev => [...prev, { ...res, dockId: null, status: 'waiting_reservation' }]);
                await updateReservation(res.id, { dockId: null, status: 'waiting_reservation', startTime: null, endTime: null }, userData, userData.tenantId);
            }
        });

        setActiveMenuId(null);
    };

    const handleChangeStatus = async (res, newStatus) => {
        if (userData?.role === 'transporter') {
            warning('Nakliyeci kullanıcıları araç statüsünü değiştiremez.');
            return;
        }
        if (!can(PERMISSIONS.RESERVATIONS, PERMISSIONS.EDIT)) {
            warning('Yetkiniz bulunmamaktadır.');
            return;
        }
        setReservations(prev => prev.map(r => r.id === res.id ? { ...r, status: newStatus } : r));
        await updateReservation(res.id, { status: newStatus }, userData, userData.tenantId);

        success('Durum: ' + (STATUS_LABELS[newStatus] || newStatus));
        setActiveMenuId(null);
    };

    const handleDelete = async (id) => {
        if (!can(PERMISSIONS.RESERVATIONS, PERMISSIONS.DELETE)) {
            warning('Silme yetkiniz bulunmamaktadır.');
            return;
        }
        showConfirm({
            title: 'Rezervasyonu Sil',
            message: 'Bu rezervasyonu silmek istediğinize emin misiniz? Bu işlem geri alınamaz.',
            type: 'error',
            confirmText: 'Evet, Sil',
            onConfirm: async () => {
                await deleteReservation(id, userData.tenantId);

                await logAuditAction(
                    id,
                    'RESERVATION',
                    'RESERVATION_DELETE',
                    { description: 'Rezervasyon silindi' },
                    userData,
                    'ReservationManagement',
                    userData.tenantId
                );

                loadData(userData.tenantId);
                setIsModalOpen(false);
                setActiveMenuId(null);
            }
        });
    };

    const handleModalSave = async (formData, isConfirm = false) => {
        const isNew = !modalData?.id;
        if (isNew && !can(PERMISSIONS.RESERVATIONS, PERMISSIONS.CREATE)) {
            warning('Yeni kayıt oluşturma yetkiniz yok.');
            return;
        }
        if (!isNew && !can(PERMISSIONS.RESERVATIONS, PERMISSIONS.EDIT)) {
            warning('Düzenleme yetkiniz yok.');
            return;
        }

        try {
            // Reconstruct Dates
            const [h, m] = formData.startTime.split(':').map(Number);
            const startDate = dayjs(selectedDate).hour(h).minute(m).second(0).toDate();
            const endDate = dayjs(startDate).add(formData.duration, 'minute').toDate();

            const selectedDock = docks.find(d => d.id === formData.dockId);

            const payload = {
                ...formData,
                startTime: startDate,
                endTime: endDate,
                // Remove duration and startTime string which were just for form
                dockId: formData.dockId,
                dockName: selectedDock?.name || ''
            };

            // Clean up form-only fields
            delete payload.duration;
            const note = payload.note;
            delete payload.note;

            if (modalData && modalData.id) {
                // CASE FOR TRANSPORTER: Can update fields and add note
                if (userData?.role === 'transporter') {
                    // Check for changes to determine status if not explicitly confirming
                    const hasFieldChanges =
                        payload.plateNumber !== modalData.plateNumber ||
                        payload.driverName !== modalData.driverName ||
                        payload.driverPhone !== modalData.driverPhone ||
                        dayjs(payload.startTime).format('HH:mm') !== dayjs(modalData.startTime).format('HH:mm') ||
                        dayjs(payload.endTime).diff(payload.startTime, 'minute') !== dayjs(modalData.endTime).diff(modalData.startTime, 'minute');

                    let finalStatus;
                    if (isConfirm) {
                        finalStatus = VEHICLE_STATUS.APPROVED;
                    } else {
                        // If just updating, move to REVISED if fields changed, otherwise keep current
                        finalStatus = hasFieldChanges ? VEHICLE_STATUS.TRANSPORTER_REVISED : modalData.status;
                    }

                    // Protect against downgrading from arrived/loading etc.
                    const currentStatusLevel = Object.values(VEHICLE_STATUS).indexOf(modalData.status);
                    const revisedStatusLevel = Object.values(VEHICLE_STATUS).indexOf(VEHICLE_STATUS.TRANSPORTER_REVISED);

                    if (currentStatusLevel >= revisedStatusLevel && !isConfirm) {
                        finalStatus = modalData.status;
                    }

                    if (note && note.trim()) {
                        await addReservationNote(modalData.id, note, userData, userData.tenantId);
                    }

                    await updateReservation(modalData.id, {
                        ...payload,
                        status: finalStatus,
                        lastActionBy: userData.uid,
                        lastActionRole: userData.role
                    }, userData, userData.tenantId);

                    success(isConfirm ? 'Rezervasyon onaylandı.' : 'Rezervasyon güncellendi.');
                    setIsModalOpen(false);
                    loadData(userData.tenantId);
                    return;
                }

                // Update
                await updateReservation(modalData.id, payload, userData, userData.tenantId);

                success('Rezervasyon güncellendi.');
            } else {
                // Create
                payload.tenantId = userData.tenantId;
                const createdId = await createReservation(payload, userData);

                success('Yeni rezervasyon oluşturuldu.');
            }

            setIsModalOpen(false);
            loadData(userData.tenantId); // Refresh timeline
        } catch (err) {
            toastError('Kaydetme hatası: ' + err.message);
        }
    };

    // Navigation
    const handleDayChange = (dir) => setSelectedDate(dayjs(selectedDate).add(dir, 'day').format('YYYY-MM-DD'));

    // Navigation Logic
    const handleTimeScroll = (dir) => {
        // dir is 1 or -1. Each step is 4 hours.
        let newHour = viewStartHour + (dir * 4);

        if (newHour >= 24) {
            newHour -= 24;
            handleDayChange(1); // Next day
        } else if (newHour < 0) {
            newHour += 24;
            handleDayChange(-1); // Prev day
        }

        setViewStartHour(newHour);
    };

    const visibleStartObj = useMemo(() => {
        return dayjs(selectedDate).hour(viewStartHour).toDate();
    }, [selectedDate, viewStartHour]);

    return (
        <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            <div className="planning-container" style={{
                height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column',
                background: 'var(--bg-primary)', padding: '24px'
            }}>
                <div className="card-premium glow-theme" style={{ marginBottom: '1.5rem', padding: '16px 24px' }}>
                    <div className="header-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
                        <div className="header-item">
                            <label className="header-label">FABRİKA</label>
                            <select className="glass-input" value={filterFactory} onChange={e => {
                                setFilterFactory(e.target.value);
                                setFilterType('all');
                                setFilterDock('all');
                            }}>
                                <option value="all">Tüm Fabrikalar</option>
                                {factories.map(f => (
                                    <option key={f.id} value={f.id}>{f.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="header-item">
                            <label className="header-label">RAMPA GRUBU</label>
                            <select className="glass-input" value={filterType} onChange={e => {
                                setFilterType(e.target.value);
                                setFilterDock('all');
                            }}>
                                <option value="all">Tümü</option>
                                {doorGroups.map(bg => (
                                    <option key={bg.id} value={bg.id}>{bg.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="header-item">
                            <label className="header-label">RAMPA</label>
                            <select className="glass-input" value={filterDock} onChange={e => setFilterDock(e.target.value)}>
                                <option value="all">Tümü</option>
                                {docks
                                    .filter(d => {
                                        // Apply same logic as list for consistency
                                        if (filterFactory !== 'all' && d.factoryId !== filterFactory) return false;
                                        if (filterType !== 'all') {
                                            if (d.doorGroupId && d.doorGroupId !== filterType) return false;
                                            if (!d.doorGroupId && d.type !== filterType) return false;
                                        }
                                        return true;
                                    })
                                    .map(d => (
                                        <option key={d.id} value={d.id}>{d.name}</option>
                                    ))
                                }
                            </select>
                        </div>
                        <div className="header-item">
                            <label className="header-label">TARİH SEÇİMİ</label>
                            <div className="flex items-center gap-1 p-1" style={{ height: 42 }}>
                                {/* Day Back */}
                                <button onClick={() => handleDayChange(-1)} className="glass-nav-btn px-2">◀</button>

                                <input
                                    type="date"
                                    value={selectedDate}
                                    onChange={(e) => setSelectedDate(e.target.value)}
                                    className="glass-date-input flex-1 mx-1"
                                />

                                {/* Day Forward */}
                                <button onClick={() => handleDayChange(1)} className="glass-nav-btn px-2">▶</button>
                            </div>
                        </div>
                    </div>
                </div>

                <div style={{ display: 'flex', gap: '24px', flex: 1, minHeight: 0 }}>
                    {/* WAITING LIST COLUMN - ULTIMATE PREMIUM REDESIGN */}
                    <div
                        className="card-premium glow-theme"
                        style={{
                            width: '320px',
                            minWidth: '320px',
                            flexShrink: 0,
                            padding: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            background: 'var(--bg-card)',
                            backdropFilter: 'blur(20px)',
                            border: '1px solid var(--border-color)',
                            borderRadius: '24px',
                            boxShadow: 'var(--shadow-md)',
                            overflow: 'hidden',
                            height: '100%' /* Ensure it takes full available height */
                        }}
                    >
                        {/* PREMIUM HEADER SECTION */}
                        <div style={{
                            padding: '30px 24px 20px 24px',
                            background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.03) 0%, transparent 100%)',
                            borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                            position: 'relative'
                        }}>
                            {/* NEON HEADER */}
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                marginBottom: '20px'
                            }}>
                                <div style={{
                                    width: '10px',
                                    height: '10px',
                                    borderRadius: '50%',
                                    backgroundColor: '#fbbf24',
                                    boxShadow: '0 0 15px #fbbf24, 0 0 5px #fbbf24'
                                }}></div>
                                <span style={{
                                    fontSize: '13px',
                                    fontWeight: '900',
                                    color: '#fbbf24',
                                    letterSpacing: '3px',
                                    textTransform: 'uppercase',
                                    textShadow: '0 0 10px rgba(251, 191, 36, 0.3)'
                                }}>
                                    BEKLEYENLER <span style={{ opacity: 0.6 }}>({waitingList.length})</span>
                                </span>
                            </div>

                            {/* NEON/LED BUTTON */}
                            {userData?.role !== 'transporter' && (
                                <button
                                    onClick={() => setIsAutoAssignModalOpen(true)}
                                    disabled={waitingList.length === 0 || !can(PERMISSIONS.RESERVATIONS, PERMISSIONS.EDIT)}
                                    style={{
                                        width: '100%',
                                        padding: '14px',
                                        borderRadius: '16px',
                                        background: 'linear-gradient(90deg, #2563eb, #3b82f6)',
                                        border: 'none',
                                        color: '#ffffff',
                                        fontSize: '12px',
                                        fontWeight: '900',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '12px',
                                        cursor: 'pointer',
                                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                        boxShadow: '0 8px 16px rgba(37, 99, 235, 0.3), 0 0 20px rgba(37, 99, 235, 0.2)',
                                        letterSpacing: '1.5px',
                                        textTransform: 'uppercase',
                                        opacity: waitingList.length === 0 ? 0.5 : 1
                                    }}
                                    onMouseOver={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                        e.currentTarget.style.boxShadow = '0 12px 24px rgba(37, 99, 235, 0.5), 0 0 30px rgba(37, 99, 235, 0.3)';
                                    }}
                                    onMouseOut={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 8px 16px rgba(37, 99, 235, 0.3), 0 0 20px rgba(37, 99, 235, 0.2)';
                                    }}
                                >
                                    <span style={{ fontSize: '18px', filter: 'drop-shadow(0 0 8px white)' }}>⚡</span>
                                    OTOMATİK ATA
                                </button>
                            )}
                        </div>

                        {/* ENFORCED SCROLLABLE AREA */}
                        <div style={{
                            flex: 1,
                            minHeight: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            padding: '10px 0'
                        }}>
                            <WaitingListArea>
                                {waitingList.map(res => <DraggableWaitingItem key={res.id} res={res} />)}
                            </WaitingListArea>
                        </div>
                    </div>

                    {/* TIMELINE BOARD */}
                    <div
                        ref={timelineWrapperRef}
                        className="card-premium glow-theme flex flex-col flex-1 min-w-0 overflow-hidden relative"
                        style={{ width: '100%' }}
                    >
                        <TimelineBoard
                            ref={timelineRef}
                            docks={filteredDocks}
                            reservations={reservations}
                            visibleStart={visibleStartObj}
                            visibleDurationMins={8 * 60}
                            pxPerMin={pxPerMin}
                            statusConfig={STATUS_CONFIG}
                            onTimeScroll={handleTimeScroll}
                            onEventContextMenu={(e, res) => {
                                if (res.isMasked) return;
                                e.preventDefault();
                                setMenuPos({ x: e.clientX, y: e.clientY });
                                setActiveMenuId(res.id);
                            }}
                            onEventClick={(e, res) => {
                                if (res.isMasked) return;
                                setModalData(res);
                                setIsModalOpen(true);
                            }}
                            canEdit={can(PERMISSIONS.RESERVATIONS, PERMISSIONS.EDIT)}
                        />
                    </div>
                </div>

                {/* STATUS LEGEND - Redesigned */}
                <div className="glass-legend-container glow-theme" style={{ marginTop: '1.5rem' }}>
                    {/* Showing key statuses for legend, excluding deprecated or internal ones if possible */}
                    {Object.values(VEHICLE_STATUS).map((key) => {
                        const conf = STATUS_CONFIG[key];
                        if (!conf) return null;
                        return (
                            <div key={key} className="glass-status-badge">
                                <span className="status-indicator" style={{ background: conf.color, boxShadow: `0 0 8px ${conf.color}40` }}></span>
                                <span className="badge-text">{conf.label}</span>
                            </div>
                        );
                    })}
                </div>

                {/* CONTEXT MENU */}
                {activeMenuId && (
                    <>
                        <div
                            style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 9998 }}
                            onClick={() => setActiveMenuId(null)}
                        />
                        <div
                            style={{ position: 'fixed', top: menuPos.y, left: menuPos.x, zIndex: 9999 }}
                            className="glass-context-menu"
                        >
                            <div className="glass-menu-header">İşlemler</div>
                            <button onClick={() => {
                                const res = reservations.find(r => r.id === activeMenuId);
                                if (userData?.role === 'transporter') {
                                    setModalData(res);
                                    setIsModalOpen(true);
                                    setActiveMenuId(null);
                                } else {
                                    navigate('/coordinator/planning', { state: { editReservation: res } });
                                }
                            }} className="glass-menu-item">
                                {userData?.role === 'transporter' ? (
                                    <><span>📝</span> Not Ekle / Detay</>
                                ) : (
                                    <><span>✏️</span> Düzenle</>
                                )}
                            </button>
                            <div className="glass-menu-divider"></div>
                            <div className="glass-menu-header">Durum Değiştir</div>
                            {userData?.role !== 'transporter' && Object.values(VEHICLE_STATUS).map((key) => {
                                const conf = STATUS_CONFIG[key];
                                if (!conf) return null;
                                return (
                                    <button key={key} onClick={() => handleChangeStatus({ id: activeMenuId }, key)}
                                        className="glass-menu-item"
                                    >
                                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: conf.color, boxShadow: '0 0 5px ' + conf.color }}></span>
                                        {conf.label}
                                    </button>
                                );
                            })}
                            {userData?.role !== 'transporter' && (
                                <>
                                    <div className="glass-menu-divider"></div>
                                    <button onClick={() => handleMoveToWaiting(reservations.find(r => r.id === activeMenuId))}
                                        className="glass-menu-item" style={{ color: '#fbbf24' }}>
                                        <span>↩</span> Bekleyenlere Al
                                    </button>
                                    <button onClick={() => handleDelete(activeMenuId)}
                                        className="glass-menu-item" style={{ color: '#f87171' }}>
                                        <span>🗑️</span> Sil
                                    </button>
                                </>
                            )}
                        </div>
                    </>
                )}

                <ReservationModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onSave={handleModalSave}
                    onDelete={handleDelete}
                    initialData={modalData}
                    docks={docks}
                    userData={userData}
                    tenantId={user?.tenantId}
                />

                <AutoAssignModal
                    isOpen={isAutoAssignModalOpen}
                    onClose={() => setIsAutoAssignModalOpen(false)}
                    onConfirm={handleAutoAssign}
                    waitingCount={waitingList.length}
                    selectedDate={selectedDate}
                />

                <ConfirmDialog
                    isOpen={confirmConfig.isOpen}
                    onClose={() => setConfirmConfig(prev => ({ ...prev, isOpen: false }))}
                    onConfirm={confirmConfig.onConfirm}
                    title={confirmConfig.title}
                    message={confirmConfig.message}
                    type={confirmConfig.type}
                    confirmText={confirmConfig.confirmText}
                    cancelText={confirmConfig.cancelText}
                />
            </div>

            <DragOverlay dropAnimation={{ sideEffects: null }}>
                {dragActiveItem && (
                    <div className="dragging-overlay glass-card-item" style={{ width: 150, height: 60, background: 'rgba(50,50,50,0.9)' }}>
                        <div className="font-bold p-2">{dragActiveItem.plateNumber}</div>
                    </div>
                )}
            </DragOverlay>
        </DndContext>
    );
};

export default ReservationManagement;
